import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import DoctorService from '../Doctor/DoctorService';
import './DoctorAppointments.css';

const DoctorAppointments = () => {
  const { doctorId, clinicId } = useParams();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const response = await DoctorService.getDoctorAppointments(doctorId, clinicId);
        if (response.status === 1 && Array.isArray(response.data)) {
          setAppointments(response.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        setError(`Failed to load appointments: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [doctorId, clinicId]);

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours));
    date.setMinutes(parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCurrency = (amount) => {
    return amount !== null ? `₹${amount}` : 'N/A';
  };

  const getAppointmentType = (type) => {
    return type === 1 ? 'In-Person' : 'Online';
  };

  // Function to parse and format notes as badges
  const formatNotes = (notes) => {
    if (!notes) return <span className="text-gray-500">None</span>;
    try {
      const notesArray = JSON.parse(notes);
      if (Array.isArray(notesArray) && notesArray.length > 0) {
        return (
          <div className="flex flex-wrap gap-1">
            {notesArray.map((note, index) => (
              <span
                key={index}
                className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full"
                title={note}
              >
                {note.length > 15 ? `${note.substring(0, 15)}...` : note}
              </span>
            ))}
          </div>
        );
      }
      return <span className="text-gray-500">None</span>;
    } catch (err) {
      console.error('Error parsing notes:', err);
      return <span className="text-red-500">Invalid Notes</span>;
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(appointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAppointments = appointments.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="doctor-appointments">
      <div className="appointments-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} /> Back
        </button>
        <h2>Doctor Appointments</h2>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">Loading appointments...</div>
      ) : (
        <div className="appointments-content">
          {appointments.length === 0 ? (
            <div className="no-appointments">
              <p>No appointments found.</p>
            </div>
          ) : (
            <>
              <div className="appointments-summary">
                <p>Showing {startIndex + 1} to {Math.min(endIndex, appointments.length)} of {appointments.length} appointments</p>
              </div>
              
              <div className="appointments-table-container">
                <table className="appointments-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Patient Name</th>
                      <th>Age</th>
                      <th>Booked On</th>
                      <th>Slot Time</th>
                      <th>Type</th>
                      <th>Speciality</th>
                      <th>Amount</th>
                      <th>Symptoms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAppointments.map((appointment) => (
                      <tr key={appointment.id}>
                        <td className="appointment-id">{appointment.id}</td>
                        <td className="patient-name">{appointment.patient_name || 'N/A'}</td>
                        <td className="patient-age">{appointment.age || 'N/A'}</td>
                        <td className="appointment-datetime">
                          {formatDateTime(appointment.appointment_datetime)}
                        </td>
                        <td className="time-slot">
                          {formatTime(appointment.from_time)} - {formatTime(appointment.to_time)}
                        </td>
                        <td>
                          <span className={`type-badge ${appointment.appointment_type === 1 ? 'in-person' : 'online'}`}>
                            {getAppointmentType(appointment.appointment_type)}
                          </span>
                        </td>
                        <td className="speciality">{appointment.speciality_name || 'N/A'}</td>
                        <td className="amount">{formatCurrency(appointment.book_amount)}</td>
                        <td className="notes">
                          {formatNotes(appointment.notes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <span>Page {currentPage} of {totalPages}</span>
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn" 
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    
                    <div className="pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn" 
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DoctorAppointments;